import { RestaurantData } from '@deskbay/shared/types';
import { resolveTargetLanguages, getMenuTranslationStatus } from '@deskbay/shared/utils/translationStatus';

export type AlertPriority = 'urgent' | 'secondary' | 'system';
export type AlertCategory = 'booking' | 'menu' | 'profile' | 'subscription' | 'marketing';

export interface ConfigAlert {
  id: string;
  priority: AlertPriority;
  category: AlertCategory;
  title: string;
  description: string;
  actionLabel: string;
  actionRoute: string;
  condition: (data: RestaurantData) => boolean;
  dismissible: boolean;
}

export const CONFIGURATION_RULES: ConfigAlert[] = [
  // BOOKING RULES
  {
    id: 'booking_table_mode_no_tables',
    priority: 'urgent',
    category: 'booking',
    title: 'booking_agent.no_tables_title',
    description: 'booking_agent.no_tables_desc',
    actionLabel: 'booking_agent.setup_tables',
    actionRoute: '/dashboard/bookings/tables',
    condition: (data) => {
      const isTableBased = data.bookingSettings.bookingMode === 'table_based';
      const hasNoTables = !data.rooms || data.rooms.length === 0 || 
        data.rooms.every(r => !r.tables || r.tables.length === 0);
      const bookingEnabled = data.customerPreferences?.calendarEnabled;
      return isTableBased && hasNoTables && !!bookingEnabled;
    },
    dismissible: false // URGENTE - non può essere chiuso
  },

  // MENU RULES
  {
    id: 'menu_not_translated',
    priority: 'secondary',
    category: 'menu',
    title: 'booking_agent.menu_not_translated_title',
    description: 'booking_agent.menu_not_translated_desc',
    actionLabel: 'booking_agent.translate_menu',
    actionRoute: '/dashboard/menu',
    condition: (data) => {
      if (!data.menu || data.menu.length === 0) return false;

      // Se il ristoratore ha disattivato il multilingue, il menu è volutamente in una sola lingua:
      // nessun avviso di "non tradotto". (Default ON: undefined/true = comportamento attuale.)
      if (data.customerPreferences?.multilingualEnabled === false) return false;

      // Fonte di verità UNICA: lo stesso agente di "Traduci tutto" e del tracker di completamento.
      // Rileva mancante/vuoto/echo/source-cambiato su TUTTE le lingue target (5 base + extra), escludendo
      // la lingua sorgente. Allinea l'avviso a ciò che l'editor mostra ed evita falsi negativi della
      // vecchia euristica "esiste almeno una chiave in translations".
      const sourceLang = data.customerPreferences?.sourceLanguage || 'it';
      const targetLangs = resolveTargetLanguages(data.customerPreferences?.additionalLanguages)
        .filter(l => l !== sourceLang);
      if (targetLangs.length === 0) return false; // nessuna lingua oltre la sorgente → niente da tradurre

      const status = getMenuTranslationStatus(data.menu, targetLangs, sourceLang);
      return !status.fullyTranslated;
    },
    dismissible: true
  },

  // MARKETING RULES
  {
    // Caso A: prima generazione — menù caricato ma strategia mai generata
    id: 'marketing_first_generation',
    priority: 'secondary',
    category: 'marketing',
    title: 'booking_agent.marketing_first_generation_title',
    description: 'booking_agent.marketing_first_generation_desc',
    actionLabel: 'booking_agent.generate_marketing',
    actionRoute: '/dashboard/marketing',
    condition: (data) => {
      // Scenario B (nessun piatto): silenzio totale
      if (!data.menu || data.menu.length === 0) return false;
      // Strategia mai generata
      return !data.customerPreferences?.marketingAIHtml;
    },
    dismissible: true
  },
  {
    // Caso B: rinnovo — strategia esistente ma cooldown 30gg scaduto
    id: 'marketing_renewal',
    priority: 'secondary',
    category: 'marketing',
    title: 'booking_agent.marketing_renewal_title',
    description: 'booking_agent.marketing_renewal_desc',
    actionLabel: 'booking_agent.generate_marketing',
    actionRoute: '/dashboard/marketing',
    condition: (data) => {
      // Scenario B (nessun piatto): silenzio totale
      if (!data.menu || data.menu.length === 0) return false;
      // Deve esistere una strategia precedente
      if (!data.customerPreferences?.marketingAIHtml) return false;
      const lastDate = data.customerPreferences?.marketingLastGenerationDate;
      if (!lastDate) return false;
      const diffDays = Math.ceil(Math.abs(new Date().getTime() - new Date(lastDate).getTime()) / (1000 * 3600 * 24));
      return diffDays >= 30;
    },
    dismissible: true
  },

  // PROFILE RULES
  {
    id: 'profile_incomplete_hours',
    priority: 'secondary',
    category: 'profile',
    title: 'booking_agent.incomplete_hours_title',
    description: 'booking_agent.incomplete_hours_desc',
    actionLabel: 'booking_agent.complete_hours',
    actionRoute: '/dashboard/profile/hours',
    condition: (data) => {
      const allClosed = data.profileInfo.openingHours.every(day => !day.isOpen);
      return allClosed;
    },
    dismissible: true
  },

  // BOOKING CONFIG RULES
  {
    id: 'booking_incomplete_config',
    priority: 'secondary',
    category: 'booking',
    title: 'booking_agent.incomplete_config_title',
    description: 'booking_agent.incomplete_config_desc',
    actionLabel: 'booking_agent.complete_config',
    actionRoute: '/dashboard/bookings/settings',
    condition: (data) => {
      const settings = data.bookingSettings;
      const enabled = data.customerPreferences?.calendarEnabled;
      
      if (!enabled) return false;
      
      // Verifica configurazioni essenziali mancanti
      const missingConfig = !settings.duration || !settings.minNotice || 
        !settings.maxAdvance || !settings.cutoffTime;
      
      return missingConfig;
    },
    dismissible: true
  },

  // CONTACT INFO RULES
  {
    id: 'profile_missing_contact',
    priority: 'secondary',
    category: 'profile',
    title: 'booking_agent.missing_contact_title',
    description: 'booking_agent.missing_contact_desc',
    actionLabel: 'booking_agent.add_contact',
    actionRoute: '/dashboard/profile/info',
    condition: (data) => {
      const business = data.businessInfo;
      const missingContact = !business.phone || !business.restaurantEmail;
      return missingContact;
    },
    dismissible: true
  },

  // BOOKING NOTIFICATION CONTACTS MISSING
  {
    id: 'booking_missing_notification_contacts',
    priority: 'urgent',
    category: 'booking',
    title: 'booking_agent.missing_notification_contacts_title',
    description: 'booking_agent.missing_notification_contacts_desc',
    actionLabel: 'booking_agent.configure_notifications',
    actionRoute: '/dashboard/bookings/settings',
    condition: (data) => {
      // Attivo solo se il sistema prenotazioni è abilitato
      const bookingEnabled = data.customerPreferences?.calendarEnabled;
      if (!bookingEnabled) return false;

      const prefs = data.customerPreferences;

      // Leggi il tipo di notifica selezionato dall'utente (da customerPreferences, NON da notification_preferences)
      const whatsAppType = prefs?.notificationWhatsAppType || 'none';
      const emailType = prefs?.notificationEmailType || 'none';

      // Se entrambi sono 'none', nessun problema (utente non vuole notifiche)
      if (whatsAppType === 'none' && emailType === 'none') return false;

      let missingWhatsApp = false;
      let missingEmail = false;

      // Controlla WhatsApp in base al tipo selezionato
      if (whatsAppType === 'custom') {
        // L'utente ha scelto numero personalizzato ma potrebbe non averlo inserito
        const customNumber = prefs?.notificationWhatsAppCustomNumber;
        const hasNumber = customNumber?.number && customNumber.number.trim().length > 0;
        missingWhatsApp = !hasNumber;
      } else if (whatsAppType === 'locale') {
        // L'utente vuole usare il numero locale del ristorante
        const localePhone = data.businessInfo.whatsappPhone;
        const hasNumber = localePhone?.number && localePhone.number.trim().length > 0;
        missingWhatsApp = !hasNumber;
      } else if (whatsAppType === 'owner') {
        // L'utente vuole usare il numero del proprietario
        const ownerPhone = data.user?.phone;
        missingWhatsApp = !ownerPhone || ownerPhone.trim().length === 0;
      }

      // Controlla Email in base al tipo selezionato
      if (emailType === 'custom') {
        // L'utente ha scelto email personalizzata ma potrebbe non averla inserita
        const customEmail = prefs?.notificationEmailCustomAddress;
        missingEmail = !customEmail || customEmail.trim().length === 0;
      } else if (emailType === 'public') {
        // L'utente vuole usare l'email pubblica del ristorante
        const publicEmail = data.businessInfo.restaurantEmail;
        missingEmail = !publicEmail || publicEmail.trim().length === 0;
      } else if (emailType === 'owner') {
        // L'utente vuole usare l'email del proprietario
        const ownerEmail = data.user?.email;
        missingEmail = !ownerEmail || ownerEmail.trim().length === 0;
      }

      return missingWhatsApp || missingEmail;
    },
    dismissible: false // URGENTE - non può essere chiuso perché blocca le notifiche
  },

  // SUBSCRIPTION RULES
  {
    id: 'subscription_expired',
    priority: 'urgent',
    category: 'subscription',
    title: 'booking_agent.subscription_expired_title',
    description: 'booking_agent.subscription_expired_desc',
    actionLabel: 'booking_agent.renew_subscription',
    actionRoute: '/dashboard/subscription',
    condition: (data) => {
      const sub = data.subscriptionStatus;
      if (!sub) return false;
      return sub.status === 'expired';
    },
    dismissible: false
  },
  {
    id: 'subscription_grace_period',
    priority: 'urgent',
    category: 'subscription',
    title: 'booking_agent.subscription_grace_period_title',
    description: 'booking_agent.subscription_grace_period_desc',
    actionLabel: 'booking_agent.update_payment',
    actionRoute: '/dashboard/subscription',
    condition: (data) => {
      const sub = data.subscriptionStatus;
      if (!sub) return false;
      return sub.status === 'grace_period' || !!sub.grace_period;
    },
    dismissible: false
  },
  {
    id: 'subscription_cancelled',
    priority: 'secondary',
    category: 'subscription',
    title: 'booking_agent.subscription_cancelled_title',
    description: 'booking_agent.subscription_cancelled_desc',
    actionLabel: 'booking_agent.reactivate_subscription',
    actionRoute: '/dashboard/subscription',
    condition: (data) => {
      const sub = data.subscriptionStatus;
      if (!sub) return false;
      return sub.status === 'cancelled';
    },
    dismissible: true
  },
  {
    id: 'subscription_expiring_soon',
    priority: 'secondary',
    category: 'subscription',
    title: 'booking_agent.subscription_expiring_soon_title',
    description: 'booking_agent.subscription_expiring_soon_desc',
    actionLabel: 'booking_agent.manage_subscription',
    actionRoute: '/dashboard/subscription',
    condition: (data) => {
      const sub = data.subscriptionStatus;
      if (!sub || !sub.expires_at) return false;
      if (sub.status !== 'active' && sub.status !== 'cancelled') return false;
      const expiresAt = new Date(sub.expires_at);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 3600 * 24));
      return daysUntilExpiry > 0 && daysUntilExpiry <= 7;
    },
    dismissible: true
  },

  // MANUAL SLOTS SCHEDULE MISMATCH
  {
    id: 'booking_manual_slots_schedule_mismatch',
    priority: 'secondary',
    category: 'booking',
    title: 'booking_agent.slots_schedule_mismatch_title',
    description: 'booking_agent.slots_schedule_mismatch_desc',
    actionLabel: 'booking_agent.review_slots',
    actionRoute: '/dashboard/bookings/settings',
    condition: (data) => {
      const bookingEnabled = data.customerPreferences?.calendarEnabled;
      const slotMode = data.bookingSettings.slotMode || 'auto';
      
      if (!bookingEnabled || slotMode !== 'manual') return false;
      
      const manualSlots = data.bookingSettings.manualSlots;
      if (!manualSlots) return false;
      
      // Map day names to keys
      const dayMap: Record<string, string> = {
        'Lunedì': 'mon',
        'Martedì': 'tue',
        'Mercoledì': 'wed',
        'Giovedì': 'thu',
        'Venerdì': 'fri',
        'Sabato': 'sat',
        'Domenica': 'sun'
      };
      
      // Check for mismatches
      let hasMismatch = false;
      
      data.profileInfo.openingHours?.forEach(day => {
        const key = dayMap[day.day] as keyof typeof manualSlots;
        if (!key) return;
        
        const slots = manualSlots[key];
        if (!slots || slots.length === 0) return;
        
        // If there are slots but day is closed
        if (!day.isOpen) {
          hasMismatch = true;
          return;
        }
        
        // If there are slots but no opening hours defined
        if (day.slots.length === 0) {
          hasMismatch = true;
          return;
        }
        
        // Check if any slot is outside business hours
        const firstSlot = day.slots[0];
        const lastSlot = day.slots[day.slots.length - 1];
        
        const [openH, openM] = firstSlot.from.split(':').map(Number);
        const [closeH, closeM] = lastSlot.to.split(':').map(Number);
        const openMinutes = openH * 60 + openM;
        // Handle midnight (00:00 should be treated as 23:59 = 1439 minutes)
        const closeMinutes = (closeH === 0 && closeM === 0) ? 1439 : closeH * 60 + closeM;
        
        slots.forEach(slot => {
          const [slotH, slotM] = slot.split(':').map(Number);
          const slotMinutes = slotH * 60 + slotM;
          
          if (slotMinutes < openMinutes || slotMinutes > closeMinutes) {
            hasMismatch = true;
          }
        });
      });
      
      return hasMismatch;
    },
    dismissible: true
  },

  // ONBOARDING PROFILE RULES
  {
    id: 'profile_missing_user',
    priority: 'secondary',
    category: 'profile',
    title: 'booking_agent.profile_missing_user_title',
    description: 'booking_agent.profile_missing_user_desc',
    actionLabel: 'booking_agent.complete_user_profile',
    actionRoute: '/dashboard/settings',
    condition: (data) => {
      const user = data.user;
      if (!user) return false;
      const hasFirstName = Boolean(user.firstName?.trim());
      const hasLastName = Boolean(user.lastName?.trim());
      return !hasFirstName || !hasLastName;
    },
    dismissible: true
  },
  {
    id: 'profile_missing_business',
    priority: 'secondary',
    category: 'profile',
    title: 'booking_agent.profile_missing_business_title',
    description: 'booking_agent.profile_missing_business_desc',
    actionLabel: 'booking_agent.complete_business_profile',
    actionRoute: '/dashboard/profile/branding',
    condition: (data) => {
      const profile = data.profileInfo;
      if (!profile) return false;
      const hasLogo = Boolean(profile.logoUrl?.trim());
      const hasBio = Boolean(profile.bio?.trim());
      return !hasLogo || !hasBio;
    },
    dismissible: true
  }
];
